import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useRubro } from "./rubro";

// ---------------------------------------------------------------------------
// SYSTEM PREVIEW — "Así se ve tu sistema." Tabbed: Oportunidades / Calendario / Reporte.
// Kanban opportunity cards are draggable between columns.
// ---------------------------------------------------------------------------

type KanbanCard = {
  id: string;
  name: string;
  amount: string;
  tag: string;
  tagBg: string;
  tagFg: string;
};

type KanbanCol = {
  name: string;
  dot: string;
  count: number;
  total: string;
  cards: KanbanCard[];
};

type CalDay = { day: string; events: CalEvent[] };
type CalEvent = {
  time: string;
  title: string;
  who: string;
  src: string;
  bg: string;
  bar: string;
};

type Report = {
  calle: string;
  calleN: string;
  ganado: string;
  consultas: string;
  visitas: string;
  items: { name: string; note: string; amount: string }[];
};

function buildKanban(): KanbanCol[] {
  return [
    {
      name: "Nuevas",
      dot: "#A78BFA",
      count: 6,
      total: "$ 2.140.000",
      cards: [
        {
          id: "opp-1",
          name: "María L.",
          amount: "$ 380.000",
          tag: "WhatsApp",
          tagBg: "rgba(139,92,246,0.16)",
          tagFg: "#C4B5FD",
        },
        {
          id: "opp-2",
          name: "Jorge P.",
          amount: "$ 420.000",
          tag: "Instagram",
          tagBg: "rgba(232,121,249,0.16)",
          tagFg: "#E879F9",
        },
      ],
    },
    {
      name: "Presupuesto enviado",
      dot: "#F2B65A",
      count: 4,
      total: "$ 3.180.000",
      cards: [
        {
          id: "opp-3",
          name: "Ana R.",
          amount: "$ 590.000",
          tag: "Seguimiento",
          tagBg: "rgba(242,182,90,0.16)",
          tagFg: "#F2B65A",
        },
        {
          id: "opp-4",
          name: "Lucas M.",
          amount: "$ 240.000",
          tag: "Día 5",
          tagBg: "rgba(242,182,90,0.16)",
          tagFg: "#F2B65A",
        },
      ],
    },
    {
      name: "Agendado",
      dot: "#34D399",
      count: 3,
      total: "$ 1.900.000",
      cards: [
        {
          id: "opp-5",
          name: "Carlos M.",
          amount: "$ 720.000",
          tag: "Agente IA",
          tagBg: "rgba(52,211,153,0.16)",
          tagFg: "#34D399",
        },
        {
          id: "opp-6",
          name: "Sofía T.",
          amount: "$ 310.000",
          tag: "Equipo",
          tagBg: "rgba(238,237,242,0.10)",
          tagFg: "#EEEDF2",
        },
      ],
    },
    {
      name: "Ganado",
      dot: "#A78BFA",
      count: 2,
      total: "$ 1.200.000",
      cards: [
        {
          id: "opp-7",
          name: "Pedro G.",
          amount: "$ 680.000",
          tag: "Cerrado",
          tagBg: "rgba(139,92,246,0.16)",
          tagFg: "#A78BFA",
        },
        {
          id: "opp-8",
          name: "Luz V.",
          amount: "$ 520.000",
          tag: "Cerrado",
          tagBg: "rgba(139,92,246,0.16)",
          tagFg: "#A78BFA",
        },
      ],
    },
  ];
}

function buildCalendar(): CalDay[] {
  return [
    {
      day: "Lun",
      events: [
        {
          time: "09:30",
          title: "Visita — María L.",
          who: "Palermo",
          src: "Agente IA",
          bg: "rgba(139,92,246,0.10)",
          bar: "#A78BFA",
        },
      ],
    },
    {
      day: "Mar",
      events: [
        {
          time: "11:00",
          title: "Medición — Jorge P.",
          who: "Caballito",
          src: "Equipo",
          bg: "rgba(238,237,242,0.06)",
          bar: "#EEEDF2",
        },
      ],
    },
    {
      day: "Mié",
      events: [
        {
          time: "15:00",
          title: "Instalación — Ana R.",
          who: "Belgrano",
          src: "Agente IA",
          bg: "rgba(139,92,246,0.10)",
          bar: "#A78BFA",
        },
      ],
    },
    {
      day: "Jue",
      events: [
        {
          time: "10:00",
          title: "Service — Carlos M.",
          who: "Palermo",
          src: "Agente IA",
          bg: "rgba(139,92,246,0.10)",
          bar: "#A78BFA",
        },
      ],
    },
    {
      day: "Vie",
      events: [
        {
          time: "14:30",
          title: "Cierre — Pedro G.",
          who: "Vos",
          src: "Equipo",
          bg: "rgba(238,237,242,0.06)",
          bar: "#EEEDF2",
        },
      ],
    },
  ];
}

function buildReport(): Report {
  return {
    calle: "$ 8.420.000",
    calleN: "15 presupuestos en la calle",
    ganado: "$ 3.150.000",
    consultas: "12",
    visitas: "5",
    items: [
      {
        name: "Ana R.",
        note: "Presupuesto enviado · día 10",
        amount: "$ 590.000",
      },
      {
        name: "Lucas M.",
        note: "Presupuesto enviado · día 5",
        amount: "$ 240.000",
      },
      { name: "María L.", note: "Agendado para el lunes", amount: "$ 380.000" },
    ],
  };
}

type TabId = "opps" | "cal" | "report";

export function HomeSystemPreview() {
  const { rb } = useRubro();
  const [tab, setTab] = useState<TabId>("opps");
  const [columns, setColumns] = useState<KanbanCol[]>(() => buildKanban());
  const [activeMenuCardId, setActiveMenuCardId] = useState<string | null>(null);
  const colRefs = useRef<(HTMLDivElement | null)[]>([]);

  const tabs: { id: TabId; label: string }[] = [
    { id: "opps", label: "Oportunidades" },
    { id: "cal", label: "Calendario" },
    { id: "report", label: "Reporte lunes" },
  ];

  const viewTitle = tabs.find((t) => t.id === tab)!.label;
  const calendar = buildCalendar();
  const report = buildReport();

  function moveCardToColumn(cardId: string, targetColIdx: number) {
    setColumns((prev) => {
      let fromIdx = -1;
      let card: KanbanCard | null = null;
      for (let i = 0; i < prev.length; i++) {
        const found = prev[i].cards.find((c) => c.id === cardId);
        if (found) {
          fromIdx = i;
          card = found;
          break;
        }
      }
      if (!card || fromIdx === targetColIdx) return prev;

      return prev.map((col, i) => {
        if (i === fromIdx) {
          return { ...col, cards: col.cards.filter((c) => c.id !== cardId) };
        }
        if (i === targetColIdx) {
          return { ...col, cards: [...col.cards, card!] };
        }
        return col;
      });
    });
    setActiveMenuCardId(null);
  }

  function handleDragEnd(
    cardId: string,
    info: { point: { x: number; y: number } },
  ) {
    let targetIdx = -1;
    for (let i = 0; i < colRefs.current.length; i++) {
      const el = colRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (
        info.point.x >= rect.left &&
        info.point.x <= rect.right &&
        info.point.y >= rect.top &&
        info.point.y <= rect.bottom
      ) {
        targetIdx = i;
        break;
      }
    }
    if (targetIdx === -1) return;
    moveCardToColumn(cardId, targetIdx);
  }

  return (
    <section
      data-screen-label="Por dentro"
      className="mx-auto px-6"
      style={{
        maxWidth: 1240,
        paddingTop: "clamp(80px,10vw,128px)",
        paddingBottom: "clamp(80px,10vw,128px)",
      }}
    >
      <div
        className="font-mono text-[13px] text-[#A78BFA]"
        style={{ letterSpacing: "0.08em" }}
      >
        03 — POR DENTRO
      </div>
      <div className="flex justify-between items-end gap-6 flex-wrap mt-5">
        <div>
          <h2
            className="text-[#EEEDF2] font-medium"
            style={{
              fontSize: "clamp(36px,4.8vw,64px)",
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              maxWidth: 760,
              textWrap: "balance" as never,
            }}
          >
            Así se ve tu sistema.
          </h2>
          <p className="mt-3.5 max-w-[560px] leading-[1.55] text-[17px] text-[#9C98A6]">
            Cada consulta se vuelve una oportunidad, cada visita queda en el
            calendario y cada lunes recibís el resumen.
          </p>
        </div>
        <div className="flex gap-1 p-1 border border-[#2A2633] rounded-full flex-wrap">
          {tabs.map((v) => {
            const active = v.id === tab;
            return (
              <button
                key={v.id}
                onClick={() => setTab(v.id)}
                className="text-[14px] font-medium rounded-full px-4 py-2 transition-colors"
                style={{
                  background: active ? "#7C3AED" : "transparent",
                  color: active ? "#fff" : "#9C98A6",
                }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="mt-10 bg-[#0F0D14] border border-[#2A2633] rounded-[20px] overflow-hidden"
        style={{ boxShadow: "0 40px 100px -50px rgba(124,58,237,0.45)" }}
      >
        <div className="flex justify-between items-center gap-3 px-5 py-3.5 border-b border-[#221F2A] flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="font-semibold text-[14px] text-[#EEEDF2]">
              {rb.biz}
            </span>
            <span className="text-[13px] text-[#9C98A6]">· {viewTitle}</span>
          </div>
          <span className="font-mono text-[11.5px] text-[#9C98A6]">
            EJEMPLO · {rb.label}
          </span>
        </div>

        {tab === "opps" && (
          <div className="overflow-x-auto p-5">
            <div
              className="grid gap-3"
              style={{
                gridTemplateColumns: "repeat(4,minmax(230px,1fr))",
                minWidth: 960,
              }}
            >
              {columns.map((col, colIdx) => (
                <div
                  key={col.name}
                  ref={(el) => {
                    colRefs.current[colIdx] = el;
                  }}
                  className="bg-[#13111A] border border-[#221F2A] rounded-[14px] p-3 flex flex-col gap-2"
                >
                  <div className="flex flex-col gap-1 px-1 pb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="rounded-full"
                        style={{ width: 8, height: 8, background: col.dot }}
                      />
                      <span className="text-[13.5px] font-semibold whitespace-nowrap text-[#EEEDF2]">
                        {col.name}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 text-[12px] text-[#9C98A6]">
                      <span>{col.cards.length} oportunidades</span>
                      <span className="font-mono whitespace-nowrap">
                        {col.total}
                      </span>
                    </div>
                  </div>
                  {col.cards.map((c) => {
                    const isMenuOpen = activeMenuCardId === c.id;
                    return (
                      <motion.div
                        key={c.id}
                        layout
                        drag
                        dragMomentum={false}
                        dragSnapToOrigin
                        dragTransition={{
                          bounceStiffness: 600,
                          bounceDamping: 20,
                        }}
                        onDragEnd={(_, info) => handleDragEnd(c.id, info)}
                        whileDrag={{
                          scale: 1.05,
                          zIndex: 50,
                          boxShadow: "0 20px 40px -10px rgba(0,0,0,0.6)",
                        }}
                        style={{ cursor: "grab" }}
                        className="relative bg-[#0A090D] border border-[#252130] hover:border-[#3A3347] rounded-[10px] px-3.5 py-3 flex flex-col gap-2 select-none group transition-colors"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="text-[14px] font-medium text-[#EEEDF2]">
                            {c.name}
                          </div>
                          {/* Botón rápido para cambiar estado */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenuCardId(isMenuOpen ? null : c.id);
                            }}
                            className="text-[11px] font-mono text-[#9C98A6] hover:text-[#EEEDF2] px-1.5 py-0.5 rounded border border-[#2A2633] hover:border-[#7C3AED] transition-colors"
                            title="Cambiar estado"
                          >
                            Mover ▾
                          </button>
                        </div>

                        {/* Menú desplegable para cambiar de estado con un clic */}
                        {isMenuOpen && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-10 right-2 z-30 bg-[#17141F] border border-[#3A3347] rounded-lg p-1.5 shadow-2xl flex flex-col gap-1 min-w-[170px]"
                          >
                            <div className="text-[10.5px] font-mono text-[#9C98A6] px-2 py-1 uppercase tracking-wider border-b border-[#2A2633]">
                              Mover a:
                            </div>
                            {columns.map((targetCol, targetIdx) => {
                              const isCurrent = targetIdx === colIdx;
                              return (
                                <button
                                  key={targetCol.name}
                                  type="button"
                                  disabled={isCurrent}
                                  onClick={() =>
                                    moveCardToColumn(c.id, targetIdx)
                                  }
                                  className={`text-left text-[12.5px] px-2.5 py-1.5 rounded flex items-center justify-between gap-2 transition-colors ${
                                    isCurrent
                                      ? "opacity-40 cursor-not-allowed text-[#6E6A7A]"
                                      : "text-[#EEEDF2] hover:bg-[#252130] hover:text-[#A78BFA]"
                                  }`}
                                >
                                  <span className="flex items-center gap-2">
                                    <span
                                      className="w-2 h-2 rounded-full"
                                      style={{ background: targetCol.dot }}
                                    />
                                    {targetCol.name}
                                  </span>
                                  {isCurrent && (
                                    <span className="text-[11px]">✓</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <div className="flex justify-between items-center gap-2">
                          <span className="text-[13px] font-mono text-[#D8D5E0]">
                            {c.amount}
                          </span>
                          <span
                            className="text-[11px] rounded-full px-2 py-0.5"
                            style={{ background: c.tagBg, color: c.tagFg }}
                          >
                            {c.tag}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-[#9C98A6] mt-3 font-mono flex items-center gap-2">
              <span>↕ Arrastrá las tarjetas entre columnas o hacé clic en</span>
              <span className="text-[#A78BFA] border border-[#3A3347] px-1.5 py-0.5 rounded text-[10.5px]">
                Mover ▾
              </span>
              <span>para cambiar su estado al instante.</span>
            </p>
          </div>
        )}

        {tab === "cal" && (
          <div className="overflow-x-auto p-5">
            <div
              className="grid gap-px bg-[#221F2A] border border-[#221F2A] rounded-[14px] overflow-hidden"
              style={{
                gridTemplateColumns: "repeat(5,minmax(180px,1fr))",
                minWidth: 900,
              }}
            >
              {calendar.map((d) => (
                <div
                  key={d.day}
                  className="bg-[#13111A] flex flex-col"
                  style={{ minHeight: 320 }}
                >
                  <div className="px-3.5 py-3 border-b border-[#221F2A] text-[13px] font-semibold text-[#BAB6C4]">
                    {d.day}
                  </div>
                  <div className="p-2.5 flex flex-col gap-2">
                    {d.events.map((e, i) => (
                      <div
                        key={i}
                        className="rounded-[10px] px-3 py-2.5 flex flex-col gap-1"
                        style={{
                          background: e.bg,
                          borderLeft: `3px solid ${e.bar}`,
                        }}
                      >
                        <span className="font-mono text-[11.5px] text-[#9C98A6]">
                          {e.time}
                        </span>
                        <span className="text-[13.5px] font-medium leading-[1.3] text-[#EEEDF2]">
                          {e.title}
                        </span>
                        <span className="text-[12px] text-[#BAB6C4]">
                          {e.who}
                        </span>
                        <span
                          className="text-[11px] mt-0.5"
                          style={{ color: e.bar }}
                        >
                          {e.src}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-5 flex-wrap mt-3.5 text-[12.5px] text-[#9C98A6]">
              <span className="flex items-center gap-1.5">
                <span
                  className="rounded-[3px]"
                  style={{ width: 10, height: 10, background: "#A78BFA" }}
                />
                Agendado por el Agente IA
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="rounded-[3px]"
                  style={{ width: 10, height: 10, background: "#EEEDF2" }}
                />
                Agendado por tu equipo
              </span>
            </div>
          </div>
        )}

        {tab === "report" && (
          <div
            className="grid gap-6"
            style={{
              padding: "clamp(20px,3vw,36px)",
              gridTemplateColumns:
                "repeat(auto-fit,minmax(min(100%,320px),1fr))",
            }}
          >
            <div className="flex flex-col gap-3">
              <div className="text-[13px] text-[#9C98A6]">
                Lunes, 8:00 · por WhatsApp y mail
              </div>
              <h3 className="text-[26px] font-medium tracking-[-0.02em] leading-[1.2] text-[#EEEDF2]">
                Buen día. Esta es tu semana en números.
              </h3>
              <div className="grid grid-cols-2 gap-2.5 mt-2">
                <div className="bg-[#13111A] border border-[#221F2A] rounded-xl p-4">
                  <div className="text-[12px] text-[#9C98A6]">
                    Plata en la calle
                  </div>
                  <div className="text-[24px] font-semibold tracking-[-0.02em] mt-1.5 text-[#EEEDF2]">
                    {report.calle}
                  </div>
                  <div className="text-[12px] text-[#6E6A7A] mt-0.5">
                    {report.calleN}
                  </div>
                </div>
                <div
                  className="rounded-xl p-4"
                  style={{
                    background: "rgba(139,92,246,0.10)",
                    border: "1px solid #3A2D63",
                  }}
                >
                  <div className="text-[12px] text-[#D3CCE6]">
                    Ganado la semana pasada
                  </div>
                  <div className="text-[24px] font-semibold tracking-[-0.02em] mt-1.5 text-[#A78BFA]">
                    {report.ganado}
                  </div>
                  <div className="text-[12px] text-[#9C98A6] mt-0.5">
                    1 presupuesto cerrado
                  </div>
                </div>
                <div className="bg-[#13111A] border border-[#221F2A] rounded-xl p-4">
                  <div className="text-[12px] text-[#9C98A6]">
                    Consultas nuevas
                  </div>
                  <div className="text-[24px] font-semibold tracking-[-0.02em] mt-1.5 text-[#EEEDF2]">
                    {report.consultas}
                  </div>
                </div>
                <div className="bg-[#13111A] border border-[#221F2A] rounded-xl p-4">
                  <div className="text-[12px] text-[#9C98A6]">
                    Visitas agendadas
                  </div>
                  <div className="text-[24px] font-semibold tracking-[-0.02em] mt-1.5 text-[#EEEDF2]">
                    {report.visitas}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#13111A] border border-[#221F2A] rounded-[14px] p-5 flex flex-col">
              <div className="text-[13px] font-semibold mb-2 text-[#EEEDF2]">
                Para mirar esta semana
              </div>
              {report.items.map((i) => (
                <div
                  key={i.name}
                  className="grid items-center py-3 border-t border-[#221F2A]"
                  style={{ gridTemplateColumns: "1fr auto", gap: 12 }}
                >
                  <div>
                    <div className="text-[14px] font-medium text-[#EEEDF2]">
                      {i.name}
                    </div>
                    <div className="text-[12.5px] text-[#9C98A6] mt-0.5">
                      {i.note}
                    </div>
                  </div>
                  <span className="text-[13px] font-mono text-[#D8D5E0]">
                    {i.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
